import React, { useState, useContext } from 'react'
import ReactDOM from 'react-dom'
import { useMutation } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import languages, { getLanguageAcronym } from '../languages.js'
import { ADD_PARAGRAPH_WITH_CONTENT_MUTATION } from '../query/gqlQueries.js'
import { streamOllamaGenerate } from '../ollama.js'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'

const MODELS = ['gemma4:26b', 'gemma3:27b']

export default function AddParagraphDialog({ onCreated }) {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)

  const [status, setStatus] = useState('idle') // idle, analyzing, reviewing, saving, completed, error
  const [errorMessage, setErrorMessage] = useState('')
  const [model, setModel] = useState('gemma4:26b')
  const [targetLanguage, setTargetLanguage] = useState('Korean')
  const [title, setTitle] = useState('')
  const [pastedText, setPastedText] = useState('')
  const [previewData, setPreviewData] = useState(null)
  const [progressMsg, setProgressMsg] = useState('')

  const [addParagraphWithContent] = useMutation(ADD_PARAGRAPH_WITH_CONTENT_MUTATION)

  const isOpen = dialogStates['dialogAddParagraph']

  const handleClose = () => {
    closeDialog('dialogAddParagraph')
    setStatus('idle')
    setPreviewData(null)
    setErrorMessage('')
    setTitle('')
    setPastedText('')
    setProgressMsg('')
  }

  const handleAnalyze = async () => {
    if (!pastedText.trim()) return
    setStatus('analyzing')
    setErrorMessage('')
    setProgressMsg('Sending paragraph to Ollama...')

    const srcLang = getLanguageAcronym(targetLanguage) || 'eng'

    const totalStartTime = performance.now()

    try {
      const prompt = `
You are an expert language teacher analyzing a paragraph for a student learning ${targetLanguage}.

TASKS:
1. Split the paragraph into natural sentences. Be smart about abbreviations, decimals, and ellipses — don't split inside them.
2. For each sentence, provide an English translation.
3. Tokenize the paragraph into every distinct word that appears (including particles, articles, and grammatical markers). For EACH word, provide:
   - "pos" — its part of speech, one of: noun, verb, adjective, adverb, pronoun, particle, conjunction, interjection, preposition, determiner, numeral, auxiliary, other
   - "translation" — an English translation of how the word is used in this paragraph

CRITICAL INSTRUCTIONS:
- Return EVERY word in the paragraph in the "words" array, not just vocabulary highlights.
- Use the surface form exactly as it appears in the paragraph (do not lemmatize).
- Tokens should not include leading/trailing punctuation.
- Return ONLY a valid JSON object. No markdown code fences, no prose.

JSON SHAPE:
{
  "sentences": [
    { "sourceText": "Text in ${targetLanguage}", "targetText": "English translation" }
  ],
  "words": [
    { "word": "Token from paragraph", "pos": "noun", "translation": "English meaning in context" }
  ]
}

PARAGRAPH:
${pastedText}
      `.trim()

      const serverUrl = process.env.REACT_APP_OLLAMA_URI || 'http://localhost:11434'

      const startTime = performance.now()
      const rawResponse = await streamOllamaGenerate({
        serverUrl,
        apiKey: process.env.REACT_APP_OLLAMA_KEY,
        body: {
          model,
          prompt,
          format: 'json',
          options: { temperature: 0.1 }
        },
        onProgress: (acc) => {
          setProgressMsg(`Analyzing paragraph… (${acc.length} chars streamed)`)
        }
      })
      const elapsed = ((performance.now() - startTime) / 1000).toFixed(2)
      console.log(`[Timer] Paragraph analysis took ${elapsed} seconds`)

      let rawJson = (rawResponse || '').replace(/```json/g, '').replace(/```/g, '').trim()
      const parsed = JSON.parse(rawJson)

      const sentences = Array.isArray(parsed.sentences) ? parsed.sentences : []
      const words = Array.isArray(parsed.words) ? parsed.words : []

      // Normalize/dedupe words
      const seen = new Set()
      const cleanWords = []
      for (const w of words) {
        if (!w || !w.word) continue
        const surface = String(w.word).trim().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
        if (!surface) continue
        const key = surface.toLowerCase()
        if (seen.has(key)) continue
        seen.add(key)
        cleanWords.push({
          word: surface,
          pos: (w.pos || 'other').toLowerCase(),
          translation: (w.translation || '').toString().trim(),
          lang: srcLang
        })
      }

      const cleanSentences = sentences
        .filter(s => s && s.sourceText)
        .map((s, idx) => ({
          sourceText: s.sourceText.trim(),
          targetText: (s.targetText || '').trim(),
          sourceLang: srcLang,
          targetLang: 'eng',
          order: idx
        }))

      const totalElapsed = ((performance.now() - totalStartTime) / 1000).toFixed(2)
      console.log(`[Timer] Total: ${totalElapsed}s | sentences=${cleanSentences.length} words=${cleanWords.length}`)

      setPreviewData({ sentences: cleanSentences, words: cleanWords })
      setStatus('reviewing')
    } catch (err) {
      console.error(err)
      setStatus('error')
      if (err.message?.includes('Failed to fetch') || err.message?.includes('Load failed')) {
        setErrorMessage('Connection to Ollama failed. The tunnel may be down or the stream was dropped — check that the Mac Studio is reachable.')
      } else if (err instanceof SyntaxError) {
        setErrorMessage('Ollama returned invalid JSON. Try again, or use a stronger model (gemma4:26b).')
      } else {
        setErrorMessage(err.message || 'Failed to analyze paragraph.')
      }
    }
  }

  const handleSave = async () => {
    if (!previewData) return
    setStatus('saving')

    const srcLang = getLanguageAcronym(targetLanguage) || 'eng'

    // Build posMap: word(lowercase) → pos
    const posMap = {}
    previewData.words.forEach(w => {
      if (w.word) posMap[w.word.toLowerCase()] = w.pos
    })

    try {
      const result = await addParagraphWithContent({
        variables: {
          input: {
            paragraph: {
              title: title || 'Untitled Paragraph',
              text: pastedText,
              lang: srcLang,
              username,
              posMap: JSON.stringify(posMap)
            },
            sentences: previewData.sentences,
            words: previewData.words
          }
        }
      })
      setStatus('completed')
      const created = result?.data?.addParagraphWithContent?.paragraph
      if (onCreated && created) onCreated(created.identity)
      setTimeout(() => handleClose(), 1500)
    } catch (err) {
      console.error(err)
      setStatus('error')
      setErrorMessage(err.message)
    }
  }

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px' }}>
        <button className={styles.closeBtn} onClick={handleClose}>
          <CloseIcon />
        </button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>AI Reading Material</div>
            <h2 className={styles.title}>New Paragraph</h2>
          </div>
        </div>

        <div className={styles.container}>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Title (optional)</div>
              <input
                type="text"
                className={styles.input}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Korean weather report"
              />
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Target Language</div>
              <select className={styles.input} value={targetLanguage} onChange={(e) => setTargetLanguage(e.target.value)}>
                {Object.entries(languages).map(([key, value]) => (
                  <option key={key} value={value}>{value}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Ollama Model</div>
            <select className={styles.input} value={model} onChange={(e) => setModel(e.target.value)}>
              {MODELS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Paragraph text (paste here)</div>
            <textarea
              className={styles.input}
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              placeholder="Paste the paragraph in the target language..."
              rows={10}
              style={{ resize: 'vertical', fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}
            />
          </div>

          {status === 'analyzing' && (
            <div style={{ color: '#6ee7b7', fontSize: '0.9rem', marginTop: '1rem', fontStyle: 'italic', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <div>Asking Ollama to split sentences, tag parts of speech, and translate words…</div>
              {progressMsg && <div style={{ fontWeight: 'bold' }}>{progressMsg}</div>}
            </div>
          )}

          {status === 'error' && (
            <div style={{ color: '#ff4d4d', fontSize: '0.9rem', marginTop: '1rem' }}>{errorMessage}</div>
          )}

          {status === 'completed' && (
            <div style={{ color: '#6ee7b7', fontSize: '0.9rem', marginTop: '1rem' }}>Paragraph saved to the database!</div>
          )}

          {status === 'reviewing' && previewData && (
            <div
              style={{
                marginTop: '1rem',
                padding: '1rem',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                maxHeight: '320px',
                overflowY: 'auto',
                border: '1px solid #3a3a42'
              }}>
              <h4 style={{ margin: '0 0 0.6rem 0', color: '#6ee7b7' }}>Preview</h4>

              <div style={{ marginBottom: '0.4rem', fontWeight: 'bold' }}>
                Sentences ({previewData.sentences?.length || 0})
              </div>
              {previewData.sentences?.map((s, i) => (
                <div key={i} style={{ fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                  <span style={{ color: '#6ee7b7' }}>{s.sourceText}</span>
                  {s.targetText && <> → {s.targetText}</>}
                </div>
              ))}

              <div style={{ marginTop: '1rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Words ({previewData.words?.length || 0})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {previewData.words?.map((w, i) => (
                  <span
                    key={i}
                    title={`${w.pos}${w.translation ? ' • ' + w.translation : ''}`}
                    style={{
                      fontSize: '0.8rem',
                      background: '#222226',
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${posColor(w.pos)}`
                    }}>
                    {w.word}
                    {w.translation ? <span style={{ opacity: 0.7 }}> = {w.translation}</span> : null}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {status !== 'reviewing' && status !== 'saving' && status !== 'completed' && (
            <button
              className={styles.primaryBtn}
              onClick={handleAnalyze}
              disabled={!pastedText.trim() || status === 'analyzing'}>
              {status === 'analyzing' ? 'Analyzing…' : 'Analyze Paragraph'}
            </button>
          )}
          {status === 'reviewing' && (
            <button className={styles.primaryBtn} onClick={handleSave}>
              Save Paragraph
            </button>
          )}
          {status === 'saving' && (
            <button className={styles.primaryBtn} disabled>Saving…</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

// Colour by part of speech — shared with the reader view. Same hues in both
// themes so the legend still teaches, but muted: a dozen saturated colours
// under every word of a paragraph is hard to read, and on the light (cream)
// background the bright set was unreadable outright.
const POS_DARK = {
  noun: '#7fd4ae', verb: '#d9ae5e', adjective: '#a99ad6', adverb: '#d18cae',
  pronoun: '#87aed6', particle: '#9aa3b0', conjunction: '#d69ba2', interjection: '#cdbb63',
  preposition: '#d49b71', determiner: '#74c2a4', numeral: '#78bcc7', auxiliary: '#b49ad6',
}
const POS_LIGHT = {
  noun: '#3B6E4F', verb: '#A07216', adjective: '#5F5199', adverb: '#9E4C72',
  pronoun: '#37628C', particle: '#6B7280', conjunction: '#98505A', interjection: '#856A0E',
  preposition: '#A05C28', determiner: '#2F7A5C', numeral: '#1E6F80', auxiliary: '#6D519B',
}
export function posColor(pos, theme = 'dark') {
  const table = theme === 'light' ? POS_LIGHT : POS_DARK
  return table[(pos || '').toLowerCase()] || (theme === 'light' ? '#8C867E' : '#6b7280')
}
